import React, {
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useRef,
} from 'react';
import PropTypes from 'prop-types';
import {
  useCurrentProductId,
  useUsers,
  useCurrentProject,
  useCurrentProjectId,
} from '@adsk/alloy-provider';
import { useGetAccountUsers, useAnalytics } from '@common/hooks';
import { StoreProvider } from '@common/StoreProvider';
import { KEY_FIELD, TRACK_EVENTS } from '@common/constants';
import { FLAG_NAMES } from '@common/flagNames';
import {
  withAdminFFs,
  useAdminFeatureFlag,
} from '@common/AdminLaunchDarklyProvider';
import { filterByActiveStatus, mapToField } from '@common/utils';
import { useDataApi } from '@adsk/alloy-helpers';
import { reducer, initialState } from './state/reducer';
import { ACTIONS } from './state/actions';
import { useCreateProjectUsers } from './hooks';
import InviteTrialMemberModal from './component';
import {
  buildGetProjectRequest,
  buildProductProjectTrialAccess,
} from './utils';
import { DEFAULT_TRIAL_ACCESS_LEVELS } from './constants';

const {
  ADMIN_FLAGS: {
    ACC_REPLACE_GET_CONSTRUCTION_APIS,
    ACC_REPLACE_WRITE_CONSTRUCTION_APIS,
  },
} = FLAG_NAMES;

const InviteTrialMemberModalContainer = ({
  onClose,
  onSubmit,
  show,
  ...rest
}) => {
  const [state, dispatch] = useReducer(reducer(), { show }, initialState);

  const currentProductId = useCurrentProductId();
  const { data: projectUsersData } = useUsers();
  const currentProject = useCurrentProject();
  const projectId = useCurrentProjectId();
  const { trackEvent } = useAnalytics();
  const createProjectUsersRef = useRef();
  const { data: projectData, doFetch: doGetProject } = useDataApi();
  const { value: useWriteConstructionApis } = useAdminFeatureFlag(
    ACC_REPLACE_WRITE_CONSTRUCTION_APIS
  );
  const { value: useGetConstructionApis } = useAdminFeatureFlag(
    ACC_REPLACE_GET_CONSTRUCTION_APIS
  );

  const projectProductIds = useMemo(
    () => mapToField(filterByActiveStatus(projectData?.products), KEY_FIELD),
    [projectData]
  );

  const sendAnalytics = useCallback(
    (type) => {
      trackEvent(
        `${TRACK_EVENTS.SCOPE.PROJECT_ADMIN}.${TRACK_EVENTS.SOURCE.QUICK_ADD}.${type}`,
        {
          emails: state.emails?.length,
          product_id: currentProductId,
          access_level: state.selectedAccessLevel,
          products: null,
          company: null,
          role: null,
          account_admin: currentProject?.accessLevels?.accountAdministrator,
          project_admin: currentProject?.accessLevels?.projectAdministrator,
        }
      );
    },
    [
      state?.selectedAccessLevel,
      state?.productsAccesses,
      trackEvent,
      currentProject?.accessLevels,
      currentProductId,
      state?.emails,
    ]
  );

  const handleClose = () => {
    sendAnalytics(TRACK_EVENTS.NAME.CANCEL);
    onClose();
  };

  const handleSubmit = (result) => {
    const emails = createProjectUsersRef.current?.emails || [];
    sendAnalytics(TRACK_EVENTS.NAME.COMPLETE);
    onSubmit({ ...result, emails });
  };

  const { createProjectUsersRequest } = useCreateProjectUsers({
    onSubmit: handleSubmit,
    dispatch,
    useWriteConstructionApis,
  });

  const { handleEmailInputChange } = useGetAccountUsers({
    includeAllMembers: false,
  });

  const onSubmitInviteMembers = ({ emails }) => {
    dispatch({
      type: ACTIONS.SET_IS_SAVING,
      payload: true,
    });

    const params = {
      emails,
      access: useWriteConstructionApis
        ? buildProductProjectTrialAccess(projectProductIds)
        : DEFAULT_TRIAL_ACCESS_LEVELS,
      companyId: null,
      selectedRoleId: null,
    };

    createProjectUsersRef.current = params;
    createProjectUsersRequest(params);
  };

  useEffect(() => {
    if (show) {
      sendAnalytics(TRACK_EVENTS.NAME.START);
    }
  }, [show, sendAnalytics]);

  useEffect(() => {
    if (!projectId || !show || !useGetConstructionApis) return;

    doGetProject(buildGetProjectRequest(projectId));
  }, [doGetProject, projectId, show, useGetConstructionApis]);

  return (
    <StoreProvider state={state} dispatch={dispatch}>
      <InviteTrialMemberModal
        {...rest}
        onClose={handleClose}
        onChangeEmailInput={handleEmailInputChange}
        onSubmitInviteMembers={onSubmitInviteMembers}
        projectUsers={projectUsersData || []}
        show={show}
      />
    </StoreProvider>
  );
};

InviteTrialMemberModalContainer.propTypes = {
  /**  handler executed when the user clicks on the Cancel button or on the Close Dialog cross */
  onClose: PropTypes.func.isRequired,
  /** handler executed after the user invites a project member */
  onSubmit: PropTypes.func.isRequired,
  /** Boolean property to set the visibility of the Modal */
  show: PropTypes.bool,
};

InviteTrialMemberModalContainer.defaultProps = {
  show: false,
};

export default withAdminFFs(InviteTrialMemberModalContainer);
